import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "ducks/state";
import { getRealtimeState, listRealtimeState } from "ducks/realtime";
import { getUserMe } from "ducks/user";
import {
  viewImmediateCareSession,
  getOneImmediateCareSession,
  loadCachedImmediateCareByImcsID,
} from "ducks/immediateCare";

export const useOneImmediateCareSession = (imcsID: string) => {
  const getState = React.useCallback(getRealtimeState({ imcsID }), [imcsID]);
  const getSession = React.useCallback(getOneImmediateCareSession({ imcsID }), [
    imcsID,
  ]);
  const { realtimeState } = useSelector(getState);
  const { immediateCare } = useSelector(getSession);
  const { currentUser } = useSelector(getUserMe);

  return [realtimeState, immediateCare, currentUser] as [
    typeof realtimeState,
    typeof immediateCare,
    typeof currentUser
  ];
};

export const useActiveImmediateCareSessions = () => {
  const dispatch: AppDispatch = useDispatch();
  const loadedSessions = React.useRef<Set<string>>(new Set());
  const [activeIndex, setActiveIndex] = React.useState(0);
  const { list } = useSelector(listRealtimeState);
  const { currentUser } = useSelector(getUserMe);

  const activeSessions = React.useMemo(() => {
    return list.filter((session) => {
      const caregivers = session?.caregivers ?? [];
      const doctors = caregivers
        .map((cg) => cg.doctID)
        .filter((doctID) => !!doctID);
      const index = doctors.indexOf(currentUser?.doctor?.doctID ?? "");

      return index !== -1;
    });
  }, [list, currentUser]);

  const activeSession = activeSessions[activeIndex] ?? activeSessions[0];
  const activeSessionImcsID = activeSession?.imcsID ?? "";

  const currentDoctor = React.useMemo(() => {
    const caregivers = activeSession?.caregivers ?? [];
    const index = caregivers.findIndex(
      (cg) => cg.doctID === (currentUser?.doctor?.doctID ?? "")
    );
    return caregivers[index];
  }, [activeSession, currentUser]);

  const nextSession = React.useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % activeSessions.length);
  }, [activeSessions.length]);

  const previousSession = React.useCallback(() => {
    setActiveIndex(
      (prev) => (prev - 1 + activeSessions.length) % activeSessions.length
    );
  }, [activeSessions.length]);

  const getSession = React.useCallback(
    getOneImmediateCareSession({ imcsID: activeSessionImcsID }),
    [activeSessionImcsID]
  );
  const { immediateCare } = useSelector(getSession);

  React.useEffect(() => {
    if (
      activeSessionImcsID &&
      !loadedSessions.current.has(activeSessionImcsID)
    ) {
      loadedSessions.current.add(activeSessionImcsID);
      Promise.all([
        dispatch(viewImmediateCareSession(activeSessionImcsID)),
        dispatch(loadCachedImmediateCareByImcsID(activeSessionImcsID)),
      ]);
    }
  }, [activeSessionImcsID, dispatch]);

  return [
    immediateCare,
    activeSession,
    currentDoctor,
    activeSessions,
    nextSession,
    previousSession,
    activeIndex,
  ] as [
    typeof immediateCare,
    typeof activeSession,
    typeof currentDoctor,
    typeof activeSessions,
    typeof nextSession,
    typeof previousSession,
    number
  ];
};
